/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { UseInternetFormReturn } from "../hooks/useInternetForm";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import {
  SkeletonText,
  SkeletonBlock,
} from "@ui-components/Skeleton/GenericSkeleton";
import { Internet } from "../InternetData";
import { LatencyTargetsTable } from "./LatencyTargetsTable";
import { P2PAddressingTable } from "./P2PAddressingTable";
import { BgpNeighborsCard } from "./BgpNeighborsCard";
import { SecondaryVlanInput } from "./SecondaryVlanInput";
import {
  p2pAddressingError as describeP2PAddressingError,
  secondaryVlanError,
} from "../internetAddressing";

export interface InternetEditModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSave: () => void;
  // Port dropdown
  portOptions: Array<{ id: number; label: string }>;
  selectedPortId: number | null;
  getPortLabelById: (id: number | null) => string;
  handlePortSelect: (label: string) => void;
  internetForm: UseInternetFormReturn;
  internet: Internet | null;
  // Loading state for save operation
  isSaving?: boolean;
}

export const InternetEditModal: React.FC<InternetEditModalProps> = ({
  isOpen,
  isLoading,
  onClose,
  onSave,
  portOptions,
  selectedPortId,
  getPortLabelById,
  handlePortSelect,
  internetForm,
  isSaving = false,
}) => {
  const { formData, updateField } = internetForm;

  // Tab state
  const [activeTab, setActiveTab] = useState<string>("internet-information");

  // Error states for validation
  const [p2pAddressingError, setP2PAddressingError] = useState<string>("");
  const [latencyTargetsError, setLatencyTargetsError] = useState<string>("");

  // Local state for tags input to preserve raw input (including trailing commas)
  const [tagsInput, setTagsInput] = useState<string>("");

  // Initialize tagInput when modal opens, reset when it closes
  useEffect(() => {
    if (isOpen) {
      if (formData.tag && formData.tag.length > 0) {
        setTagsInput(formData.tag.join(", "));
      } else {
        setTagsInput("");
      }
    } else {
      setTagsInput("");
      setP2PAddressingError("");
      setLatencyTargetsError("");
      setActiveTab("internet-information");
    }
  }, [isOpen]); // Only sync when modal opens/closes, not when formData.tag changes (to preserve trailing commas)

  // Sync tagsInput when data finishes loading (only if tagsInput is still empty, meaning user hasn't typed yet)
  useEffect(() => {
    if (isOpen && !isLoading && tagsInput === "") {
      if (formData.tag && formData.tag.length > 0) {
        setTagsInput(formData.tag.join(", "));
      }
    }
  }, [isOpen, isLoading]);

  const tabs = [
    { label: "Internet Information", value: "internet-information" },
    { label: "P2P Addressing", value: "p2p-addressing" },
  ];

  const handleStatusToggle = (isActive: boolean) => {
    // Switch: false = Draft (0), true = Active (1)
    updateField("isDraft", isActive ? 1 : 0);
  };
  const isActive = formData.isDraft === 1;

  // Determine if location dropdowns should be disabled (disabled on Tab 2)
  const isLocationDisabled = activeTab === "p2p-addressing";

  // P2P is optional on edit, but each leg is all or nothing.
  const p2pValidationError = useMemo(
    () => describeP2PAddressingError(formData.p2pAddressing, { requirePrimary: false }),
    [formData.p2pAddressing]
  );
  const vlanSecondaryError = secondaryVlanError(
    formData.vlan,
    formData.p2pAddressing.vlanSecondary
  );

  // Validate Latency Targets - check if there are any incomplete entries
  const isLatencyTargetsValid = useMemo(() => {
    if (!formData.latencyTargets) return true; // Empty is valid (optional)

    // Check each hostname -> interface -> IP entry
    for (const [hostname, interfaces] of Object.entries(
      formData.latencyTargets
    )) {
      if (!hostname || hostname.trim() === "") return false;
      for (const [interfaceName, ips] of Object.entries(interfaces)) {
        if (!interfaceName || interfaceName.trim() === "") return false;
        if (!Array.isArray(ips) || ips.length === 0) return false;
        for (const ip of ips) {
          if (!ip || ip.trim() === "") return false;
        }
      }
    }
    return true;
  }, [formData.latencyTargets]);

  // Handle Back button click - return to Internet Information tab
  const handleBack = () => {
    setActiveTab("internet-information");
    // Clear errors when going back
    setP2PAddressingError("");
    setLatencyTargetsError("");
  };

  // Handle Save button click - validate before saving
  const handleSave = () => {
    // Clear previous errors
    setP2PAddressingError("");
    setLatencyTargetsError("");

    // The secondary VLAN lives on the first tab; send the user back to it.
    if (vlanSecondaryError) {
      setActiveTab("internet-information");
      return;
    }

    // Validate P2P addressing
    if (p2pValidationError) {
      setP2PAddressingError(p2pValidationError);
      return;
    }

    // Validate Latency Targets
    if (!isLatencyTargetsValid) {
      setLatencyTargetsError(
        "Please fill in all fields for Latency Targets rows (Hostname, Interface, and IP) or remove incomplete rows."
      );
      return;
    }

    // If validation passes, proceed with save
    onSave();
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Internet Service"
      customWidth="max-w-6xl"
    >
      <div className="flex flex-col max-h-[80vh] h-[900px] overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-6">
              <div>
                <SkeletonBlock h={20} w="30%" className="mb-3" />
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: 10 }).map((_, idx) => (
                    <SkeletonText key={idx} lines={2} lineHeight={14} />
                  ))}
                </div>
              </div>
              <div>
                <SkeletonBlock h={20} w="40%" className="mb-3" />
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <SkeletonText key={idx} lines={2} lineHeight={14} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Tabs Navigation */}
              <GenericTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />

              {/* Tab Content */}
              {activeTab === "internet-information" && (
                <>
                  {/* Port Selection - Always visible */}
                  <GenericCard isCollapsible={false} className="p-5">
                    <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                      Location Selection
                    </h3>
                    <div className="flex flex-col space-y-4">
                      <div className="flex flex-col space-y-2">
                        <label className="text-sm font-medium">
                          Kontrol Edge Port
                        </label>
                        <GenericDropdown
                          items={portOptions.map((p) => p.label)}
                          value={getPortLabelById(selectedPortId)}
                          onSelect={handlePortSelect}
                          disabled={isLocationDisabled}
                          defaultLabel="Select Port"
                          minWidthClass="min-w-[300px]"
                          maxWidthClass="max-w-[400px]"
                        />
                      </div>
                    </div>
                  </GenericCard>
                  {/* Internet Information Section */}
                  <GenericCard isCollapsible={false} className="p-5">
                    {/* Status Toggle Switch */}
                    <div className="flex items-center justify-end gap-2 mb-4">
                      <label className="text-sm font-medium text-semantic-text-secondary flex items-center gap-1">
                        Status:
                        <GenericTooltip
                          text="You can toggle the status of this configuration by using the switch."
                          position="top"
                        />
                      </label>
                      <span
                        className={`text-sm ${
                          !isActive
                            ? "font-semibold text-semantic-text-primary"
                            : "text-semantic-text-secondary"
                        }`}
                      >
                        Draft
                      </span>
                      <GenericSwitch
                        checked={isActive}
                        onChange={handleStatusToggle}
                      />
                      <span
                        className={`text-sm ${
                          isActive
                            ? "font-semibold text-semantic-text-primary"
                            : "text-semantic-text-secondary"
                        }`}
                      >
                        Active
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <GenericInput
                        label="Name"
                        value={formData.name}
                        onChange={(value) => updateField("name", value)}
                      />
                      <GenericInput
                        label="Subscription ID"
                        value={formData.subscriptionId}
                        onChange={(value) =>
                          updateField("subscriptionId", value)
                        }
                      />
                      <GenericInput
                        label="Mbps"
                        value={formData.mbps ? String(formData.mbps) : ""}
                        onChange={(value) =>
                          updateField("mbps", value ? Number(value) : null)
                        }
                        type="number"
                      />
                      <GenericInput
                        label="VLAN"
                        value={formData.vlan ? String(formData.vlan) : ""}
                        onChange={(value) =>
                          updateField("vlan", value ? Number(value) : null)
                        }
                        type="number"
                      />
                      <SecondaryVlanInput
                        p2pAddressing={formData.p2pAddressing}
                        error={vlanSecondaryError}
                        onUpdate={(p2p) => updateField("p2pAddressing", p2p)}
                      />
                      <GenericInput
                        label="Country Code"
                        value={formData.countryCode}
                        onChange={(value) => updateField("countryCode", value)}
                      />
                      <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Routing
                        </label>
                        <GenericDropdown
                          items={[
                            "static",
                            "staticPrivate",
                            "staticPublic",
                            "dynamicPublic",
                            "dynamicPrivate",
                          ]}
                          value={formData.routing}
                          onSelect={(value) => updateField("routing", value)}
                          defaultLabel="Select Routing"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Automatic VLAN
                        </label>
                        <GenericDropdown
                          items={["Yes", "No"]}
                          value={
                            formData.automaticVlan === null
                              ? ""
                              : formData.automaticVlan
                              ? "Yes"
                              : "No"
                          }
                          defaultLabel="Select Automatic VLAN"
                          onSelect={(val) =>
                            updateField("automaticVlan", val === "Yes")
                          }
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Redundancy
                        </label>
                        <GenericDropdown
                          items={["Yes", "No"]}
                          value={
                            formData.redundancy === null
                              ? ""
                              : formData.redundancy
                              ? "Yes"
                              : "No"
                          }
                          defaultLabel="Select Redundancy"
                          onSelect={(val) =>
                            updateField("redundancy", val === "Yes")
                          }
                        />
                      </div>
                      <GenericInput 
                    label="Tags" 
                    value={tagsInput} 
                    onChange={(value) => {
                      // Update local state to preserve raw input (including trailing commas)
                      setTagsInput(value);
                      // Convert to array for parent state (for change detection), but keep the raw string in local state
                      // This allows the user to type commas without them being removed
                      const tags = value.split(",").map(t => t.trim()).filter(Boolean);
                      updateField("tag", tags);
                    }} 
                    placeholder="Enter tags separated by commas"
                  />
                    </div>
                  </GenericCard>
                </>
              )}

              {activeTab === "p2p-addressing" && (
                <>
                  {/* Error message for P2P addressing validation */}
                  {p2pAddressingError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600 font-medium">
                        {p2pAddressingError}
                      </p>
                    </div>
                  )}
                  <P2PAddressingTable
                    p2pAddressing={formData.p2pAddressing}
                    onUpdate={(p2pData) => {
                      updateField("p2pAddressing", p2pData);
                      // Clear error when user updates P2P addressing
                      if (p2pAddressingError) {
                        setP2PAddressingError("");
                      }
                    }}
                  />
                  <BgpNeighborsCard
                    bgpConfig={formData.bgpConfig}
                    onUpdate={(bgpConfig) => updateField("bgpConfig", bgpConfig)}
                  />

                  {/* Error message for Latency Targets validation */}
                  {latencyTargetsError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md mt-4">
                      <p className="text-sm text-red-600 font-medium">
                        {latencyTargetsError}
                      </p>
                    </div>
                  )}
                  <LatencyTargetsTable
                    latencyTargets={formData.latencyTargets}
                    onUpdate={(latencyData) => {
                      updateField("latencyTargets", latencyData);
                      // Clear error when user updates latency targets
                      if (latencyTargetsError) {
                        setLatencyTargetsError("");
                      }
                    }}
                  />
                </>
              )}
            </div>
          )}
        </div>
        {/* Fixed buttons at bottom */}
        <div className="flex justify-end gap-2 px-1 mt-6 pt-4 flex-shrink-0">
          {activeTab === "internet-information" ? (
            <>
              <GenericButton
                label="Cancel"
                variant="secondary"
                onClick={onClose}
                disabled={isSaving}
              />
              <GenericButton
                label="Next"
                variant="brand"
                onClick={() => {
                  setActiveTab("p2p-addressing");
                }}
                disabled={isSaving}
              />
            </>
          ) : (
            <>
              <GenericButton
                label="Back"
                variant="secondary"
                onClick={handleBack}
                
                disabled={isSaving}
              />
              <GenericButton
                label="Save"
                variant="brand"
                onClick={handleSave}
                disabled={isSaving}
                loading={isSaving}
              />
            </>
          )}
        </div>
      </div>
    </GenericModal>
  );
};
